import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Paper,
} from "@mui/material";
import { useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import { useCreateCustomerMutation, useUpdateCustomerMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { ICustomer } from "../../../Api/Interface/api.interface";
import { useStyles } from "../../../theme/appStyles";
import {
  validateEmail,
  validatePhoneNumber,
  formatPhoneNumber,
} from "../../../Components/Validation/validationUtils";
import { uploadImage } from "../../../Components/UploadFile/upload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import axios from "axios";


const CANADIAN_PROVINCES_EDIT = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

const PROVINCE_NAME_TO_CODE_EDIT: Record<string, string> = {
  Alberta: "AB", "British Columbia": "BC", Manitoba: "MB",
  "New Brunswick": "NB", "Newfoundland and Labrador": "NL",
  "Nova Scotia": "NS", "Northwest Territories": "NT",
  Nunavut: "NU", Ontario: "ON", "Prince Edward Island": "PE",
  Quebec: "QC", Saskatchewan: "SK", Yukon: "YT",
};

const TIME_SLOTS_EDIT = [
  "Morning (8:00 AM – 10:00 AM)",
  "Late Morning (10:00 AM – 12:00 PM)",
  "Afternoon (12:00 PM – 2:00 PM)",
  "Mid Afternoon (2:00 PM – 4:00 PM)",
  "Late Afternoon (4:00 PM – 6:00 PM)",
  "Evening (6:00 PM – 8:00 PM)",
  "Anytime",
  "Weekdays Only",
  "Weekends Only",
];

interface NominatimResultEdit {
  display_name: string;
  address: {
    house_number?: string;
    road?: string;
    city?: string;
    town?: string;
    village?: string;
    county?: string;
    state?: string;
    postcode?: string;
  };
}

interface ITaxpopup {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: ICustomer;
}

interface State {
  id: number;
  code: string;
  default_name: string;
}

const Customer = ({
  openModel = false,
  handleCloseDialog,
  data,
}: ITaxpopup) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [newCustomer] = useCreateCustomerMutation();
  const [updateCustomer, { isLoading }] = useUpdateCustomerMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const [dupClient, setDupClient] = useState<{ id: number; name: string; phone: string; email: string } | null>(null);
  const handleClose = () => {
    handleCloseDialog(false);
  };
  const [selectedImage, setSelectedImage] = useState<File | undefined>(
    undefined
  );
  const [isImageUploading, setIsImageUploading] = useState(false);
  const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;
  const API_URL = process.env.REACT_APP_API_URL;
  const [states, setStates] = useState([]);

  // ── Address autocomplete ──────────────────────────────────────────────────
  const [addrSuggestionsEdit, setAddrSuggestionsEdit] = useState<Array<{ street: string; city: string; province: string; postalCode: string; display: string }>>([]);
  const [addrOpenEdit, setAddrOpenEdit] = useState(false);
  const [addrLoadingEdit, setAddrLoadingEdit] = useState(false);
  const addrTimerEdit = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchAddressEdit = (val: string) => {
    formik.setFieldValue("customerStreetName", val);
    if (addrTimerEdit.current) clearTimeout(addrTimerEdit.current);
    if (val.trim().length < 4) { setAddrSuggestionsEdit([]); setAddrOpenEdit(false); return; }
    setAddrOpenEdit(true);
    addrTimerEdit.current = setTimeout(async () => {
      setAddrLoadingEdit(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(val)}&countrycodes=ca&addressdetails=1&limit=6`;
        const res = await fetch(url, { headers: { "Accept-Language": "en" } });
        const data: NominatimResultEdit[] = await res.json();
        setAddrSuggestionsEdit(data.map(item => ({
          street: [item.address?.house_number, item.address?.road].filter(Boolean).join(" ") || item.display_name.split(",")[0],
          city: item.address?.city || item.address?.town || item.address?.village || item.address?.county || "",
          province: PROVINCE_NAME_TO_CODE_EDIT[item.address?.state || ""] || item.address?.state || "",
          postalCode: item.address?.postcode || "",
          display: item.display_name,
        })));
      } catch { setAddrSuggestionsEdit([]); } finally { setAddrLoadingEdit(false); }
    }, 450);
  };

  const applyAddressEdit = (s: { street: string; city: string; province: string; postalCode: string }) => {
    const parts = s.street.split(" ");
    const streetNum = /^\d+/.test(parts[0]) ? parts[0] : "";
    const streetName = streetNum ? parts.slice(1).join(" ") : s.street;
    formik.setFieldValue("customerStreetNumber", streetNum);
    formik.setFieldValue("customerStreetName", streetName);
    formik.setFieldValue("customerCity", s.city);
    formik.setFieldValue("customerProvince", s.province);
    formik.setFieldValue("customerPostalCode", s.postalCode);
    setAddrSuggestionsEdit([]);
    setAddrOpenEdit(false);
  };

  const formik = useFormik({
    initialValues: {
      id: data?.id,
      customerFirstName: data?.customerFirstName || "",
      customerLastName: data?.customerLastName || "",
      customerMobileNum: data?.customerMobileNum || "",
      customerEmail: data?.customerEmail || "",
   
      customerProvince: data?.customerProvince || "",
      customerCity: data?.customerCity || "",
      customerPostalCode: data?.customerPostalCode || "",
      customerStreetNumber: data?.customerStreetNumber || "",
      customerStreetName: data?.customerStreetName || "",
     
   
      customerDescription: data?.customerDescription || "",
      customerCompanyName: data?.customerCompanyName || "",
      customerCompanyPhoneNumebr: data?.customerCompanyPhoneNumebr || "",
      customerCompanyEmail: data?.customerCompanyEmail || "",
      customerPreferTimeToCall: data?.customerPreferTimeToCall || "",
      customerImg: data?.customerImg || "",
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};

      if (!values.customerCompanyName.trim()) errors.customerCompanyName = "Name is required";

      const companyEmailError = validateEmail(values.customerCompanyEmail);
      if (companyEmailError) {
        errors.customerCompanyEmail = companyEmailError;
      }

      const customerEmailError = validateEmail(values.customerEmail);
      if (customerEmailError) {
        errors.customerEmail = customerEmailError;
      }

      const companyMobileNumberError = validatePhoneNumber(
        values.customerCompanyPhoneNumebr
      );
      if (companyMobileNumberError) {
        errors.customerCompanyPhoneNumebr = companyMobileNumberError;
      }

      const cutomerMobileNumberError = validatePhoneNumber(
        values.customerMobileNum
      );
      if (cutomerMobileNumberError) {
        errors.customerMobileNum = cutomerMobileNumberError;
      }

      const cutomerAltMobileNumberError = validatePhoneNumber(
        values.customerMobileNum
      );
      if (cutomerAltMobileNumberError) {
        errors.customerMobileNum = cutomerAltMobileNumberError;
      }

      return errors;
    },
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        let customerImg: string | undefined = data?.customerImg;

        if (selectedImage) {
          setIsImageUploading(true);
          formData.append("customerImg", selectedImage);
          customerImg = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }

        const temData = {
          id: values?.id ?? 0,
          customerFirstName: values.customerFirstName,
          customerLastName: values.customerLastName,
          customerMobileNum: values.customerMobileNum,
          customerEmail: values.customerEmail,

          customerProvince: values.customerProvince,
          customerCity: values.customerCity,
          customerPostalCode: values.customerPostalCode,
          customerStreetNumber: values.customerStreetNumber,
          customerStreetName: values.customerStreetName,

          customerDescription: values.customerDescription,
          customerCompanyName: values.customerCompanyName,
          customerCompanyPhoneNumebr: values.customerCompanyPhoneNumebr,
          customerCompanyEmail: values.customerCompanyEmail,
          customerPreferTimeToCall: values.customerPreferTimeToCall,
          customerImg,
        };

        const addCompanyResponse = await updateCustomer(temData).unwrap();
        if (!addCompanyResponse.status) {
          if ((addCompanyResponse as any).field === "customerCompanyPhoneNumebr") {
            formik.setFieldError("customerCompanyPhoneNumebr", "Phone number already in use");
            formik.setFieldTouched("customerCompanyPhoneNumebr", true, false);
            if ((addCompanyResponse as any).existingClient) {
              setDupClient((addCompanyResponse as any).existingClient);
            } else {
              showErrorMessage(addCompanyResponse.message);
            }
          } else {
            showErrorMessage(addCompanyResponse.message);
          }
        } else {
          showMessage(addCompanyResponse.message);
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

   useEffect(() => {
        const headers = {
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        };
      // Fetch states from the API
      axios.get(`${API_URL}/country-states`, { headers }) // Adjust API route as needed
        .then(response => {
          setStates(response.data); // Ensure your API returns an array of states
        })
        .catch(error => {
          console.error("Error fetching states:", error);
        });
    }, []);

  const formValid = useMemo(() => {
    return formik.values.customerMobileNum === "" ||
      formik.values.customerMobileNum === undefined
      ? false
      : true;
  }, [formik]);

  const resizeImage = (
    file: File,
    maxWidth: number,
    maxHeight: number
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let width = img.width;
          let height = img.height;

          // Calculate the new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile);
            } else {
              reject(new Error("Canvas is empty"));
            }
          }, file.type);
        };

        img.onerror = (error) => reject(error);
      };
    });
  };

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
      const maxSize = 2048 * 1024;

      if (!validTypes.includes(file.type)) {
        showErrorMessage("The file must be an image (jpeg, png, jpg, gif).");
        setSelectedImage(undefined);
      } else if (file.size > maxSize) {
        showErrorMessage("The file must be smaller than 2 MB.");
        setSelectedImage(undefined);
      } else {
        try {
          const resizedImage = await resizeImage(file, 500, 500);
          setSelectedImage(resizedImage);
        } catch (error) {
          showErrorMessage("Failed to resize the image.");
          setSelectedImage(undefined);
        }
      }
    } else {
      setSelectedImage(undefined);
    }
  };

  return (
    <>
    <Dialog
      open={openModel}
      onClose={handleClose}
      PaperProps={{
        style: {
          maxWidth: "1700px",
        },
      }}
    >
      <Grid className={classes.Dialog}>
        <Grid container spacing={3}>
          <Grid item lg={3} md={3} sm={12} xs={12}>
            <Card className={classes.stepperCard}>
              <Grid
                container
                justifyContent={"space-between"}
                spacing={5}
                sx={{ borderBottom: 2, padding: 2 }}
                className={classes.titleGrid}
              >
                <Grid
                  item
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  pt={2}
                  alignContent={"center"}
                >
                  <Typography className={classes.cardTitle}>
                    Edit Client
                  </Typography>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item lg={9} md={9} sm={12} xs={12}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                

                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Card className={classes.card}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                          Company / School / Person Information
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3} p={"24px"}>
                      {/* Company / Org Name */}
                      <Grid item lg={6} md={6} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">Company / Org Name</Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Enter Company / School / Org name"
                              size="small"
                              {...formik.getFieldProps("customerCompanyName")}
                              className={classes.textField}
                              InputProps={{ sx: { fontSize: 14 } }}
                              InputLabelProps={{ sx: { fontSize: 14 } }}
                              error={formik.touched.customerCompanyName && Boolean(formik.errors.customerCompanyName)}
                              helperText={formik.touched.customerCompanyName && formik.errors.customerCompanyName}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Company Phone */}
                      <Grid item lg={6} md={6} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">Company Phone</Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="(416) 555-0100"
                              size="small"
                              {...formik.getFieldProps("customerCompanyPhoneNumebr")}
                              onChange={(e) => {
                                formik.setFieldValue("customerCompanyPhoneNumebr", formatPhoneNumber(e.target.value));
                                if (formik.errors.customerCompanyPhoneNumebr) {
                                  formik.setFieldError("customerCompanyPhoneNumebr", undefined);
                                }
                              }}
                              className={classes.textField}
                              InputProps={{ sx: { fontSize: 14 } }}
                              InputLabelProps={{ sx: { fontSize: 14 } }}
                              error={formik.touched.customerCompanyPhoneNumebr && Boolean(formik.errors.customerCompanyPhoneNumebr)}
                              helperText={formik.touched.customerCompanyPhoneNumebr && formik.errors.customerCompanyPhoneNumebr}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Company Email */}
                      <Grid item lg={6} md={6} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">Company E-mail</Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Enter Company E-mail"
                              size="small"
                              type="email"
                              {...formik.getFieldProps("customerCompanyEmail")}
                              className={classes.textField}
                              InputProps={{ sx: { fontSize: 14 } }}
                              InputLabelProps={{ sx: { fontSize: 14 } }}
                              error={formik.touched.customerCompanyEmail && Boolean(formik.errors.customerCompanyEmail)}
                              helperText={formik.touched.customerCompanyEmail && formik.errors.customerCompanyEmail}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                       <Grid item lg={6} md={6} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Prefer Time to call
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Select
                              size="small"
                              displayEmpty
                              {...formik.getFieldProps("customerPreferTimeToCall")}
                              className={classes.textField}
                              fullWidth
                              sx={{ fontSize: 14 }}
                            >
                              <MenuItem value="" disabled sx={{ fontSize: 14, color: "#94a3b8" }}>
                                Select preferred time
                              </MenuItem>
                              {TIME_SLOTS_EDIT.map((slot) => (
                                <MenuItem key={slot} value={slot} sx={{ fontSize: 14 }}>
                                  {slot}
                                </MenuItem>
                              ))}
                            </Select>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid container spacing={3} p={"24px"}>

                      {/* Contact Name (optional) */}
                      <Grid item lg={6} md={6} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">Contact Name <span style={{ color: "#94a3b8", fontSize: 12 }}>(optional)</span></Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Enter contact person name"
                              size="small"
                              {...formik.getFieldProps("customerLastName")}
                              className={classes.textField}
                              InputProps={{ sx: { fontSize: 14 } }}
                              InputLabelProps={{ sx: { fontSize: 14 } }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid item lg={6} md={6} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Phone Number
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="(416) 555-0100"
                              size="small"
                              {...formik.getFieldProps("customerMobileNum")}
                              onChange={(e) => formik.setFieldValue("customerMobileNum", formatPhoneNumber(e.target.value))}
                              className={classes.textField}
                              InputProps={{ sx: { fontSize: 14 } }}
                              InputLabelProps={{ sx: { fontSize: 14 } }}
                              error={formik.touched.customerMobileNum && Boolean(formik.errors.customerMobileNum)}
                              helperText={formik.touched.customerMobileNum && formik.errors.customerMobileNum}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item lg={6} md={6} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">E-mail</Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Enter E-mail"
                              size="small"
                              type="email"
                              {...formik.getFieldProps("customerEmail")}
                              className={classes.textField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              error={
                                formik.touched.customerEmail &&
                                Boolean(formik.errors.customerEmail)
                              }
                              helperText={
                                formik.touched.customerEmail &&
                                formik.errors.customerEmail
                              }
                            />
                          </Grid>
                        </Grid>
                      </Grid>                  

                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              Note
                            </Typography>
                          </Grid> 
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Enter Note .."
                              size="small"
                              multiline
                              rows={8}
                              {...formik.getFieldProps("customerDescription")}
                              className={classes.discriptionTextField}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>




                    </Grid>
                  </Card>
                </Grid>

                {/* Address Information */}
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Card className={classes.card}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid item lg={12} md={12} sm={12} xs={12} pt={2} alignContent={"center"}>
                        <Typography className={classes.cardTitle}>
                          Address Information <span style={{ fontSize: 12, fontWeight: 400, color: "#94a3b8" }}>(optional)</span>
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container spacing={3} p={"24px"}>
                      {/* Street Address with autocomplete */}
                      <Grid item lg={8} md={8} sm={12} xs={12}>
                        <Typography variant="subtitle1">Address <span style={{ fontSize: 12, color: "#94a3b8" }}>(optional — type to search)</span></Typography>
                        <Box sx={{ position: "relative" }}>
                          <TextField
                            placeholder="Start typing street address…"
                            size="small"
                            value={`${formik.values.customerStreetNumber ? formik.values.customerStreetNumber + " " : ""}${formik.values.customerStreetName}`}
                            onChange={(e) => searchAddressEdit(e.target.value)}
                            onFocus={() => { if (addrSuggestionsEdit.length > 0) setAddrOpenEdit(true); }}
                            onBlur={() => setTimeout(() => setAddrOpenEdit(false), 200)}
                            className={classes.textField}
                            InputProps={{
                              endAdornment: addrLoadingEdit
                                ? <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>
                                : undefined,
                              sx: { fontSize: 14 },
                            }}
                          />
                          {addrOpenEdit && addrSuggestionsEdit.length > 0 && (
                            <Paper sx={{
                              position: "absolute", top: "100%", left: 0, right: 0, zIndex: 9999,
                              maxHeight: 240, overflowY: "auto", border: "1px solid #e2e8f0",
                              borderRadius: 1.5, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", mt: 0.5,
                            }}>
                              {addrSuggestionsEdit.map((s, i) => (
                                <Box
                                  key={i}
                                  onMouseDown={() => applyAddressEdit(s)}
                                  sx={{ px: 2, py: 1, cursor: "pointer", "&:hover": { bgcolor: "#eff6ff" }, borderBottom: "1px solid #f1f5f9" }}
                                >
                                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{s.street}</Typography>
                                  <Typography sx={{ fontSize: 11, color: "#64748b" }} noWrap>{s.display}</Typography>
                                </Box>
                              ))}
                            </Paper>
                          )}
                        </Box>
                      </Grid>

                      <Grid item lg={4} md={4} sm={12} xs={12}>
                        <Typography variant="subtitle1">Street No. <span style={{ fontSize: 12, color: "#94a3b8" }}>(optional)</span></Typography>
                        <TextField
                          placeholder="e.g. 123"
                          size="small"
                          {...formik.getFieldProps("customerStreetNumber")}
                          className={classes.textField}
                          InputProps={{ sx: { fontSize: 14 } }}
                        />
                      </Grid>

                      <Grid item lg={4} md={4} sm={12} xs={12}>
                        <Typography variant="subtitle1">City <span style={{ fontSize: 12, color: "#94a3b8" }}>(optional)</span></Typography>
                        <TextField
                          placeholder="City"
                          size="small"
                          {...formik.getFieldProps("customerCity")}
                          className={classes.textField}
                          InputProps={{ sx: { fontSize: 14 } }}
                        />
                      </Grid>

                      <Grid item lg={4} md={4} sm={12} xs={12}>
                        <Typography variant="subtitle1">Province <span style={{ fontSize: 12, color: "#94a3b8" }}>(optional)</span></Typography>
                        <Select
                          size="small"
                          displayEmpty
                          {...formik.getFieldProps("customerProvince")}
                          className={classes.textField}
                          fullWidth
                        >
                          <MenuItem value="">Select Province</MenuItem>
                          {CANADIAN_PROVINCES_EDIT.map((p) => (
                            <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>
                          ))}
                        </Select>
                      </Grid>

                      <Grid item lg={4} md={4} sm={12} xs={12}>
                        <Typography variant="subtitle1">Postal Code <span style={{ fontSize: 12, color: "#94a3b8" }}>(optional)</span></Typography>
                        <TextField
                          placeholder="e.g. M1A 1A1"
                          size="small"
                          {...formik.getFieldProps("customerPostalCode")}
                          onChange={(e) => formik.setFieldValue("customerPostalCode", e.target.value.toUpperCase())}
                          className={classes.textField}
                          InputProps={{ sx: { fontSize: 14 } }}
                        />
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                {/* <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Card className={classes.card}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                          Client Information
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3} p={"24px"}>
                     
                    
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">Address</Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <TextField
                              placeholder="Enter Address"
                              size="small"
                              {...formik.getFieldProps("customerAddress")}
                              className={classes.discriptionTextField}
                              multiline
                              rows={3}
                              InputProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                              InputLabelProps={{
                                sx: {
                                  fontSize: 14,
                                },
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    
                    </Grid>
                  </Card>
                </Grid> */}



 {/* <Grid item lg={12} md={12} sm={12} xs={12}>
              <Card className={classes.card}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  spacing={5}
                  sx={{ borderBottom: 2, padding: 2 }}
                  className={classes.titleGrid}
                >
                  <Grid
                    item
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    pt={2}
                    alignContent={"center"}
                  >
                    <Typography className={classes.cardTitle}>
                      Customer Address
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container spacing={3} p={"24px"}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                           Street No.
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Enter Street No."
                          size="small"
                          {...formik.getFieldProps("customerStreetNumber")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                           Street Name
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Enter Street Name"
                          size="small"
                          {...formik.getFieldProps("customerStreetName")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
               
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                           City
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Enter City"
                          size="small"
                          {...formik.getFieldProps("customerCity")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  
                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                           Province
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Select
                         size="small"
                         displayEmpty
                          {...formik.getFieldProps("customerProvince")}
                          className={classes.textField}
                          fullWidth
                        >
                            <MenuItem value="" disabled>
                                        Select Province
                                      </MenuItem>
                                      {states.map((state: State) => (
                                        <MenuItem key={state.id} value={state.code}>
                                          {state.default_name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                      </Grid>
                    </Grid>
                  </Grid>


                  <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Grid container alignItems="center">
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Typography variant="subtitle1">
                           Postal Code
                        </Typography>
                      </Grid>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <TextField
                          placeholder="Enter Postal Code"
                          size="small"
                          {...formik.getFieldProps("customerPostalCode")}
                          className={classes.textField}
                          InputProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                          InputLabelProps={{
                            sx: {
                              fontSize: 14,
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                            
                </Grid>
              </Card>
            </Grid> */}


            <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Card className={classes.card}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                          Image
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container spacing={2} p={"24px"}>
                      <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Box className={classes.imgBox}>
                          {selectedImage && (
                            <img
                              
                              src={URL.createObjectURL(selectedImage)}
                              alt="Uploaded"
                              className={classes.SelectImageBox}
                            />
                          )}
                          {!selectedImage && data && data.customerImg ? (
                            <img
                              alt="Selected"
                              height="160px"
                              src={`${LARAVEL_API_URL}/${data.customerImg}`}
                              style={{ position: "inherit" }}
                              className={classes.SelectImageBox}
                            />
                          ) : (
                            <></>
                          )}
                          <div>
                            <input
                              accept="image/*"
                              style={{ display: "none" }}
                              id="icon-button-file-1"
                              type="file"
                              onChange={handleImageChange}
                            />
                            <div
                              style={{
                                textAlign: "center",
                              }}
                            >
                              <label htmlFor="icon-button-file-1">
                                <IconButton
                                  color="primary"
                                  aria-label="upload picture"
                                  component="span"
                                >
                                  <CloudUploadIcon sx={{ fontSize: 45 }} />
                                </IconButton>
                              </label>

                              <div>PNG, JPG and JPEG are allowed</div>
                            </div>
                          </div>
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>

                <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                  <Card className={classes.card}>
                    <Grid container spacing={3} p={"24px"}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          className={classes.addButton}
                          onClick={handleClose}
                        >
                          Cancel
                        </Button>
                        <Box m={0.5}></Box>

                        <Button
                          variant="contained"
                          startIcon={<SaveAltIcon />}
                          className={classes.addButton}
                          onClick={() => formik.handleSubmit()}
                         // disabled={!formValid || isLoading}
                           disabled={isLoading}
                        >
                          Save
                        </Button>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>

    {/* ── Duplicate phone modal ────────────────────────────────────────────── */}
    <Dialog
      open={!!dupClient}
      onClose={() => setDupClient(null)}
      maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <Box sx={{ height: 4, bgcolor: "#f59e0b" }} />
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: "#d97706" }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>
          Duplicate Phone Number
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Typography sx={{ fontSize: 13, color: "#64748b", mb: 2 }}>
          This phone number is already used by another client:
        </Typography>
        {dupClient && (
          <Box sx={{ bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 2, p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonOutlinedIcon sx={{ fontSize: 15, color: "#64748b" }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{dupClient!.name}</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PhoneOutlinedIcon sx={{ fontSize: 15, color: "#64748b" }} />
              <Typography sx={{ fontSize: 13, color: "#475569" }}>{dupClient!.phone}</Typography>
            </Box>
            {dupClient!.email && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <EmailOutlinedIcon sx={{ fontSize: 15, color: "#64748b" }} />
                <Typography sx={{ fontSize: 13, color: "#475569" }}>{dupClient!.email}</Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={() => setDupClient(null)}
          sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: 1.5, borderColor: "#e2e8f0", color: "#64748b", "&:hover": { borderColor: "#94a3b8" } }}
        >
          Enter New Number
        </Button>
        <Button
          variant="contained"
          endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
          onClick={() => { const id = dupClient?.id; setDupClient(null); handleClose(); navigate(`/customer-history/${id}`); }}
          sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: 1.5, bgcolor: "#2f3891", boxShadow: "none", "&:hover": { bgcolor: "#252e78" } }}
        >
          Go to Client Profile
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default Customer;
